import { useState } from 'react'
import { useDebouncedValue } from '@/lib/hooks'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Plus, Search, Filter, Briefcase, MapPin, Clock } from 'lucide-react'
import { jobsApi } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { formatRelativeTime } from '@/lib/utils'

const statusColors: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  published: 'bg-success/10 text-success',
  paused: 'bg-warning/10 text-warning',
  closed: 'bg-muted text-muted-foreground',
}

export function JobsPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const [status, setStatus] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', { search: debouncedSearch, status }],
    queryFn: () =>
      jobsApi.list({
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(status && { status }),
      }),
  })

  const jobs = (data as any)?.items || []

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search jobs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-8"
        />
      </div>
      <div className="flex gap-1">
        {['all', 'draft', 'published', 'closed'].map((s) => (
          <Button
            key={s}
            variant={status === s || (s === 'all' && !status) ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatus(s === 'all' ? undefined : s)}
            className="h-8 text-xs"
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>
      <Button size="sm" asChild>
        <Link to="/jobs/new">
          <Plus className="h-4 w-4 mr-1" />
          Create
        </Link>
      </Button>
    </div>
  )

  return (
    <PageLayout fullHeight toolbar={toolbar}>
      {/* Jobs List */}
      <div className="h-full lg:overflow-y-auto">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-lg" />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Briefcase className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-1">No jobs found</h3>
              <p className="text-muted-foreground mb-4">
                {search
                  ? 'Try adjusting your search'
                  : 'Create your first job posting to start hiring'}
              </p>
              {!search && (
                <Button asChild>
                  <Link to="/jobs/new">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Job
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((job: any, index: number) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15, delay: index * 0.03 }}
              >
                <Link to="/jobs/$jobId" params={{ jobId: job.id }}>
                  <Card interactive>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold truncate">{job.title}</h3>
                            <Badge
                              className={statusColors[job.status] || statusColors.draft}
                            >
                              {job.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {job.department && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="h-3 w-3" />
                                {job.department.name}
                              </span>
                            )}
                            {job.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {job.location.name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatRelativeTime(job.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <div className="text-xl font-bold">
                            {job.applicationCount || 0}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            applicants
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  )
}
